import { CreateModelUrlParameters } from '../engine/parameters/parameterlist.js';
import { TransformFileHostUrls } from '../engine/io/fileutils.js';
import { ImportSettings } from '../engine/import/importer.js';
import { AddDomElement } from '../engine/viewer/domutils.js';
import { Viewer } from '../engine/viewer/viewer.js';
import { HashHandler } from './hashhandler.js';
import { ThreeModelLoaderUI } from './threemodelloaderui.js';
import { Direction } from '../engine/geometry/geometry.js';
import { InputFilesFromFileObjects, InputFilesFromUrls } from '../engine/import/importerfiles.js';
import { EnvironmentSettings } from '../engine/viewer/shadingmodel.js';

// Pages allowed to hand this frame a model to open. The landing on the main
// domain embeds the viewer and forwards files dropped onto it; nobody else
// gets to push content into a frame of ours.
const TrustedParents = [
    'https://volumetrix.ru',
    'https://www.volumetrix.ru',
    'https://dev.volumetrix.ru'
];

export class Embed
{
    constructor (parameters)
    {
        this.parameters = parameters;
        this.viewer = new Viewer ();
        this.hashHandler = new HashHandler ();
        this.modelLoaderUI = new ThreeModelLoaderUI ();
    }

    Load ()
    {
        let canvas = AddDomElement (this.parameters.viewerDiv, 'canvas');
        this.viewer.Init (canvas);
        this.Resize ();
        this.ApplyHashSettings ();
        this.ListenToParent ();
        this.AcceptDroppedFiles ();

        if (this.hashHandler.HasHash ()) {
            let urls = this.hashHandler.GetModelFilesFromHash ();
            if (urls !== null) {
                TransformFileHostUrls (urls);
                this.LoadInputFiles (InputFilesFromUrls (urls));
                let hashParameters = CreateModelUrlParameters (urls);
                let websiteUrl = this.parameters.websiteLinkDiv.getAttribute ('href') + '#' + hashParameters;
                this.parameters.websiteLinkDiv.setAttribute ('href', websiteUrl);
            }
        }

		window.addEventListener ('resize', () => {
			this.Resize ();
		});
    }

    // Everything the hash says about how to show the model, applied once: the
    // environment map, the projection, colours and edges.
    ApplyHashSettings ()
    {
        let envMapName = 'fishermans_bastion';
        let bgIsEnvMap = false;
        let environmentSettingsObj = this.hashHandler.GetEnvironmentSettingsFromHash ();
        if (environmentSettingsObj !== null) {
            envMapName = environmentSettingsObj.environmentMapName;
            bgIsEnvMap = environmentSettingsObj.backgroundIsEnvMap;
        }
        let envMapPath = 'assets/envmaps/' + envMapName + '/';
        let envMapTextures = [
            envMapPath + 'posx.jpg',
            envMapPath + 'negx.jpg',
            envMapPath + 'posy.jpg',
            envMapPath + 'negy.jpg',
            envMapPath + 'posz.jpg',
            envMapPath + 'negz.jpg'
        ];
        let environmentSettings = new EnvironmentSettings (envMapTextures, bgIsEnvMap);
        this.viewer.SetEnvironmentMapSettings (environmentSettings);

        let projectionMode = this.hashHandler.GetProjectionModeFromHash ();
        if (projectionMode !== null) {
            this.viewer.SetProjectionMode (projectionMode);
        }
        let background = this.hashHandler.GetBackgroundFromHash ();
        if (background !== null) {
            this.viewer.SetBackgroundColor (background);
        }
        let edgeSettings = this.hashHandler.GetEdgeSettingsFromHash ();
        if (edgeSettings !== null) {
            this.viewer.SetEdgeSettings (edgeSettings);
        }
    }

    ImportSettingsFromHash ()
    {
        let settings = new ImportSettings ();
        let defaultColor = this.hashHandler.GetDefaultColorFromHash ();
        if (defaultColor !== null) {
            settings.defaultColor = defaultColor;
        }
        let defaultLineColor = this.hashHandler.GetDefaultLineColorFromHash ();
        if (defaultLineColor !== null) {
            settings.defaultLineColor = defaultLineColor;
        }
        return settings;
    }

    LoadInputFiles (inputFiles)
    {
        this.modelLoaderUI.LoadModel (inputFiles, this.ImportSettingsFromHash (), {
            onStart : () =>
            {

            },
            onFinish : (importResult, threeObject) =>
            {
                this.OnModelFinished (threeObject);
                this.TellParent ({ type : 'volumetrix:loaded', name : importResult.mainFile });
            },
            onRender : () =>
            {
                this.viewer.Render ();
            },
            onError : (importError) =>
            {
                this.TellParent ({ type : 'volumetrix:error', name : importError.mainFile });
            }
        });
    }

    // The page that embeds the frame may send it files or addresses to open.
    // Only pages we know, and only a message shaped the way we expect: a frame
    // that opens whatever anybody posts to it is a frame anybody can use.
    ListenToParent ()
    {
        window.addEventListener ('message', (ev) => {
            if (!this.IsTrustedOrigin (ev.origin)) {
                return;
            }
            let data = ev.data;
            if (data === null || typeof data !== 'object' || data.type !== 'volumetrix:open') {
                return;
            }
            if (Array.isArray (data.files) && data.files.length > 0) {
                this.LoadInputFiles (InputFilesFromFileObjects (data.files));
            } else if (Array.isArray (data.urls) && data.urls.length > 0) {
                let urls = data.urls.filter ((url) => typeof url === 'string');
                TransformFileHostUrls (urls);
                this.LoadInputFiles (InputFilesFromUrls (urls));
            }
        });
    }

    IsTrustedOrigin (origin)
    {
        return origin === window.location.origin || TrustedParents.includes (origin);
    }

    TellParent (message)
    {
        if (window.parent === window) {
            return;
        }
        // The message carries a file name and nothing else, so it may go to
        // whichever page holds the frame.
        window.parent.postMessage (message, '*');
    }

    // A model dropped straight onto the frame opens in it. Without this the
    // browser would navigate the frame to the file and show its bytes.
    AcceptDroppedFiles ()
    {
        window.addEventListener ('dragover', (ev) => {
            ev.preventDefault ();
            ev.dataTransfer.dropEffect = 'copy';
        });
        window.addEventListener ('drop', (ev) => {
            ev.preventDefault ();
            let files = Array.from (ev.dataTransfer.files);
            if (files.length > 0) {
                this.LoadInputFiles (InputFilesFromFileObjects (files));
            }
        });
    }

    Resize ()
    {
        let windowWidth = window.innerWidth;
        let windowHeight = window.innerHeight;
        this.viewer.Resize (windowWidth, windowHeight);
    }

    OnModelFinished (threeObject)
    {
        this.viewer.SetMainObject (threeObject);
        let boundingSphere = this.viewer.GetBoundingSphere ((meshUserData) => {
            return true;
        });
        this.viewer.AdjustClippingPlanesToSphere (boundingSphere);
        let camera = this.hashHandler.GetCameraFromHash ();
        if (camera !== null) {
            this.viewer.SetCamera (camera);
        } else {
            this.viewer.SetUpVector (Direction.Y, false);
            this.viewer.FitSphereToWindow (boundingSphere, false);
        }
    }
}
